import type { ImportLocalImage } from '../../../types/import';
import {
  blobToDataUrl,
  createLocalImageId,
  ensureDataUrlMime,
  IMPORT_IMAGE_ACCEPT,
  isPdfMime,
  MAX_IMPORT_IMAGE_BYTES,
  normalizeImportMime,
} from '../utils/imageHelpers';

type BrowserFile = {
  name: string;
  type: string;
  size: number;
};

type BrowserFileList = ArrayLike<BrowserFile> & Iterable<BrowserFile>;

async function filesToLocalImages(files: BrowserFileList | BrowserFile[]): Promise<ImportLocalImage[]> {
  const list = Array.from(files);
  const images: ImportLocalImage[] = [];

  for (const file of list) {
    const mimeFromName = file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : file.type;
    const mime = normalizeImportMime(mimeFromName || file.type);
    if (!mime) {
      throw new Error('Only JPEG, PNG, WebP, or PDF files are supported');
    }
    if (file.size > MAX_IMPORT_IMAGE_BYTES) {
      throw new Error('Each file must be under 10MB');
    }

    const dataUrl = ensureDataUrlMime(await blobToDataUrl(file as unknown as Blob), mime);
    const previewUri = isPdfMime(mime)
      ? undefined
      : URL.createObjectURL(file as unknown as Blob);

    images.push({
      id: createLocalImageId(),
      name: file.name || `import-${images.length + 1}`,
      mimeType: mime,
      dataUrl,
      previewUri,
      byteLength: file.size,
    });
  }

  return images;
}

/**
 * Web: open a hidden file input. Gallery / Files both resolve to the OS picker.
 */
export async function pickFromGallery(): Promise<ImportLocalImage[]> {
  return new Promise((resolve, reject) => {
    const doc = (globalThis as unknown as { document?: {
      createElement: (tag: string) => {
        type: string;
        accept: string;
        multiple: boolean;
        style: { display: string };
        files: BrowserFileList | null;
        onchange: ((ev: unknown) => void) | null;
        oncancel: ((ev: unknown) => void) | null;
        click: () => void;
        remove: () => void;
        value: string;
      };
      body: { appendChild: (node: unknown) => void };
    } }).document;

    if (!doc) {
      resolve([]);
      return;
    }

    const input = doc.createElement('input');
    input.type = 'file';
    input.accept = IMPORT_IMAGE_ACCEPT;
    input.multiple = true;
    input.style.display = 'none';
    doc.body.appendChild(input);

    const cleanup = () => {
      input.remove();
    };

    input.onchange = () => {
      const files = input.files;
      cleanup();
      if (!files?.length) {
        resolve([]);
        return;
      }
      filesToLocalImages(files).then(resolve).catch(reject);
    };

    input.oncancel = () => {
      cleanup();
      resolve([]);
    };

    input.click();
  });
}

export async function pickFromDocuments(): Promise<ImportLocalImage[]> {
  return pickFromGallery();
}

export async function filesFromDrop(fileList: BrowserFileList | BrowserFile[]): Promise<ImportLocalImage[]> {
  return filesToLocalImages(fileList);
}
